/**
 * Fix for Also Bought app - Ensures pack-of-x products add with correct pack quantity
 * Handles tags like pack-of-5, pack-of-12, pack-of-10-only, etc.
 * Exception: pack-of-10 (without -only) allows singles, so it's not modified
 * This script uses an Add-then-Update pattern: let the app add the item,
 * then immediately update the quantity if needed
 */

(function() {
  'use strict';

  // Cache for product tags to avoid multiple fetches
  const productTagsCache = {};

  /**
   * Fetch product tags from Shopify product JSON endpoint
   */
  async function getProductTags(variantId) {
    // Check cache first
    if (productTagsCache[variantId]) {
      return productTagsCache[variantId];
    }

    try {
      // Find the product handle from the Also Bought section
      const products = document.querySelectorAll('.cbb-also-bought-product');
      let productHandle = null;

      for (const product of products) {
        const link = product.querySelector('a[data-href]');
        if (link) {
          const href = link.getAttribute('data-href');
          productHandle = href.split('/').pop();

          // Fetch product data
          const response = await fetch(`/products/${productHandle}.js`);
          if (response.ok) {
            const productData = await response.json();

            // Check if this variant belongs to this product
            const variant = productData.variants.find(v => v.id === variantId);
            if (variant) {
              productTagsCache[variantId] = productData.tags;
              return productData.tags;
            }
          }
        }
      }

      return [];
    } catch (error) {
      console.error('Error fetching product tags:', error);
      return [];
    }
  }

  /**
   * Extract pack quantity from product tags
   * Returns the pack quantity (number) if found, or null otherwise
   * Handles: pack-of-5, pack-of-12, pack-of-10-only, etc.
   * Exception: pack-of-10 (without -only) returns null (allows singles)
   */
  function getPackQuantity(tags) {
    for (const tag of tags) {
      const tagLower = tag.toLowerCase();

      // Match pack-of-{number}-only or pack-of-{number}
      const match = tagLower.match(/^pack-of-(\d+)(-only)?$/);

      if (match) {
        const quantity = parseInt(match[1], 10);
        const isOnly = match[2] === '-only';

        // Special case: pack-of-10 (without -only) allows singles, so skip it
        if (quantity === 10 && !isOnly) {
          continue;
        }

        // Return the pack quantity
        return quantity;
      }
    }

    return null;
  }

  /**
   * Update cart item quantity to the specified pack quantity
   */
  async function updateCartQuantity(variantId, packQuantity) {
    try {
      console.log(`Updating variant ${variantId} quantity to ${packQuantity}...`);

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [variantId]: packQuantity
          }
        })
      });

      if (response.ok) {
        const cart = await response.json();
        console.log(`Successfully updated to quantity ${packQuantity} for variant ${variantId}`);

        // Trigger cart update events so the theme updates the cart UI
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

        return cart;
      } else {
        console.error('Failed to update cart quantity:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  }

  // Store the original fetch function
  const originalFetch = window.fetch;

  /**
   * Override fetch to intercept cart/add.js responses
   */
  window.fetch = async function(url, options) {
    // Call the original fetch
    const response = await originalFetch.apply(this, arguments);

    // Check if this is a cart add request
    if (typeof url === 'string' && url.includes('/cart/add.js')) {
      // Clone the response so we can read it without consuming it
      const clonedResponse = response.clone();

      try {
        // Parse the response to get the added items
        const responseData = await clonedResponse.json();

        // Check if this was from Also Bought (has the special property)
        const items = responseData.items || [responseData];

        for (const item of items) {
          // Check if this was added via Also Bought
          if (item.properties && item.properties['__Added with Also Bought button'] !== undefined) {
            // Only process if quantity is 1 (the default from Also Bought)
            if (item.quantity === 1 && item.variant_id) {
              // Fetch product tags
              const tags = await getProductTags(item.variant_id);

              // Check if product requires a specific pack quantity
              const packQuantity = getPackQuantity(tags);
              if (packQuantity) {
                console.log(`Pack-of-${packQuantity} product detected (variant ${item.variant_id}), updating from 1 to ${packQuantity}`);
                await updateCartQuantity(item.variant_id, packQuantity);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error processing cart add response:', error);
      }
    }

    return response;
  };

  console.log('Pack-of-x fix loaded (Add-then-Update pattern): Also Bought products with pack-of-x tags will be updated to their pack quantities');
})();
